import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen py-12" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-8 w-8 text-primary" />
            <h1 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Terms of Service
            </h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: January 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              1. Acceptance of Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using HabeshaTown, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              2. User Responsibilities
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              As a user of HabeshaTown, you agree to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Accurate Information:</strong> Provide accurate and truthful information in your listings and profile.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Legal Compliance:</strong> Follow all applicable Canadian federal, provincial, and local laws.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Listing Responsibility:</strong> Take full responsibility for the content and accuracy of your listings.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Respectful Conduct:</strong> Treat other users with respect and professionalism.</span>
              </li>
            </ul>
          </section>

          <section className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4 text-orange-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              3. Disclaimer of Liability
            </h2>
            <p className="text-orange-700 leading-relaxed mb-4">
              <strong>HabeshaTown is a platform that connects buyers and sellers. We are NOT responsible for:</strong>
            </p>
            <ul className="space-y-2 text-orange-700">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Transactions between users</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Quality, safety, or legality of listed items</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Accuracy of listing information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Disputes between buyers and sellers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></span>
                <span>Any damages resulting from use of the platform</span>
              </li>
            </ul>
            <p className="text-orange-700 leading-relaxed mt-4">
              Users are encouraged to meet in public places and verify items before completing transactions.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              4. Prohibited Items and Activities
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              The following are prohibited on HabeshaTown:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                <span>Illegal items or services under Canadian law</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                <span>Fraudulent or deceptive listings</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                <span>Counterfeit or stolen goods</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                <span>Weapons, drugs, or controlled substances</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0"></span>
                <span>Harassment, spam, or abusive behavior</span>
              </li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              5. Platform Rights
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              HabeshaTown reserves the right to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Remove any listing that violates our terms or community guidelines</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Suspend or terminate user accounts for violations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Modify or discontinue services at any time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Update these terms with notice to users</span>
              </li>
            </ul>
          </section>

          <section className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              6. Canada Only
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              This platform is intended for use by residents of Canada only. All transactions and services should be conducted within Canada and in compliance with Canadian laws.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              7. Contact Us
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about these Terms of Service, please contact us through our{' '}
              <Link to="/support" className="text-primary hover:underline">Help & Support</Link> page.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
